import {
  GetB2bCampaignOneResponse,
  ValueSharingBonusFiscalRule,
} from '@Common/types/bo/b2b/campaigns';
import { GetMigratedCampaignOneResponse } from '@Common/types/bo/b2b/campaigns/migrated';

import { makeChoicesCommon } from '@sh4res/bo-common/utils';

export const b2bCampaignStatusChoices = makeChoicesCommon<
  Exclude<GetB2bCampaignOneResponse['status'], null>
>()({
  campaign_started: 'resources.bo_common.b2b.campaigns.choices.status.campaign_started',
  campaign_ended: 'resources.bo_common.b2b.campaigns.choices.status.campaign_ended',
  campaign_investment_amount:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_investment_amount',
  campaign_investment_amount_failed:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_investment_amount_failed',
  company_funds_await: 'resources.bo_common.b2b.campaigns.choices.status.company_funds_await',
  company_funds_received: 'resources.bo_common.b2b.campaigns.choices.status.company_funds_received',
  finished: 'resources.bo_common.b2b.campaigns.choices.status.finished',
  campaign_draft: 'resources.bo_common.b2b.campaigns.choices.status.campaign_draft',
  campaign_draft_failed: 'resources.bo_common.b2b.campaigns.choices.status.campaign_draft_failed',
  campaign_ending_grace_period:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_ending_grace_period',
  campaign_ending_grace_period_failed:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_ending_grace_period_failed',
  campaign_unused_vacation_days_cash_conversion:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_unused_vacation_days_cash_conversion',
  campaign_unused_vacation_days_cash_conversion_failed:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_unused_vacation_days_cash_conversion_failed',
  campaign_pending_allocation_computation:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_pending_allocation_computation',
  campaign_pending_allocation_computation_failed:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_pending_allocation_computation_failed',
  campaign_awaiting_customer_confirmation:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_awaiting_customer_confirmation',
  campaign_awaiting_customer_confirmation_failed:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_awaiting_customer_confirmation_failed',
  campaign_company_allocation_choices_processed:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_company_allocation_choices_processed',
  campaign_company_allocation_choices_processed_failed:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_company_allocation_choices_processed_failed',
  campaign_published: 'resources.bo_common.b2b.campaigns.choices.status.campaign_published',
  campaign_published_failed:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_published_failed',
  campaign_cancelled: 'resources.bo_common.b2b.campaigns.choices.status.campaign_cancelled',
  campaign_started_failed:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_started_failed',
  campaign_ended_failed: 'resources.bo_common.b2b.campaigns.choices.status.campaign_ended_failed',
  company_funds_await_failed:
    'resources.bo_common.b2b.campaigns.choices.status.company_funds_await_failed',
  company_funds_received_failed:
    'resources.bo_common.b2b.campaigns.choices.status.company_funds_received_failed',
  finished_failed: 'resources.bo_common.b2b.campaigns.choices.status.finished_failed',
  campaign_cancelled_failed:
    'resources.bo_common.b2b.campaigns.choices.status.campaign_cancelled_failed',
});

export const b2bCampaignAmountTypeInputChoices = makeChoicesCommon<'net' | 'gross'>()({
  net: 'resources.bo_common.b2b.campaigns.choices.amount_type_input.net',
  gross: 'resources.bo_common.b2b.campaigns.choices.amount_type_input.gross',
});

export type B2bCampaignOperationPaymentMethodChoice = 'sepa_linked' | 'transfer'; // | 'sepa_new'

export const b2bCampaignOperationPaymentMethodChoices =
  makeChoicesCommon<B2bCampaignOperationPaymentMethodChoice>()({
    transfer: 'resources.bo_common.b2b.campaigns.choices.payment_methods.transfer',
    sepa_linked: 'resources.bo_common.b2b.campaigns.choices.payment_methods.sepa_linked',
    // sepa_new: 'resources.bo_common.b2b.campaigns.choices.payment_methods.sepa_new',
  });

export const b2bCampaignTypeChoices = makeChoicesCommon<GetB2bCampaignOneResponse['type']>()({
  participation: 'resources.bo_common.b2b.campaigns.choices.type.participation',
  profit_sharing: 'resources.bo_common.b2b.campaigns.choices.type.profit_sharing',
  value_sharing_bonus: 'resources.bo_common.b2b.campaigns.choices.type.value_sharing_bonus',
  voluntary_deposit: 'resources.bo_common.b2b.campaigns.choices.type.voluntary_deposit',
  unused_vacation_days: 'resources.bo_common.b2b.campaigns.choices.type.unused_vacation_days',
  ccb_matured_election: 'resources.bo_common.b2b.campaigns.choices.type.ccb_matured_election',
  withdrawal_at_maturity: 'resources.bo_common.b2b.campaigns.choices.type.withdrawal_at_maturity',
  matching_contribution: 'resources.bo_common.b2b.campaigns.choices.type.matching_contribution',
});

export const b2bCampaignPaymentMethodChoices = makeChoicesCommon<
  NonNullable<GetB2bCampaignOneResponse['payment_method']>
>()({
  sepa: 'resources.bo_common.b2b.campaigns.choices.payment_method.sepa',
  transfer: 'resources.bo_common.b2b.campaigns.choices.payment_method.transfer',
});

export const b2bCampaignReasonChoices = makeChoicesCommon<
  NonNullable<GetB2bCampaignOneResponse['reason']>
>()({
  standard: 'resources.bo_common.b2b.campaigns.choices.reason.standard',
  adjustment: 'resources.bo_common.b2b.campaigns.choices.reason.adjustment',
  regularization: 'resources.bo_common.b2b.campaigns.choices.reason.regularization',
  exceptional: 'resources.bo_common.b2b.campaigns.choices.reason.exceptional',
  omission: 'resources.bo_common.b2b.campaigns.choices.reason.omission',
});

export const valueSharingBonusFiscalRuleChoices = makeChoicesCommon<ValueSharingBonusFiscalRule>()({
  with_csg_crds_deduction:
    'resources.bo_common.b2b.campaigns.choices.value_sharing_bonus_fiscal_rule.with_csg_crds_deduction',
  exempt_from_csg_crds:
    'resources.bo_common.b2b.campaigns.choices.value_sharing_bonus_fiscal_rule.exempt_from_csg_crds',
  without_csg_crds_deduction:
    'resources.bo_common.b2b.campaigns.choices.value_sharing_bonus_fiscal_rule.without_csg_crds_deduction',
});

const migratedStatusBase = 'resources.bo_common.b2b.campaigns.migrated.choices.status';

export const b2bMigratedCampaignStatusChoices = makeChoicesCommon<string>()({
  OK: `${migratedStatusBase}.completed`,
  CPTA: `${migratedStatusBase}.completed`,
});

const migratedSubtypeBase = 'resources.bo_common.b2b.campaigns.migrated.choices.subtype';

export const b2bMigratedCampaignSubtypeChoices = makeChoicesCommon<
  NonNullable<GetMigratedCampaignOneResponse['subtype']>
>()({
  AUGCAP: `${migratedSubtypeBase}.AUGCAP`,
  LIBRE: `${migratedSubtypeBase}.LIBRE`,
  REALLOCATION: `${migratedSubtypeBase}.REALLOCATION`,
  ECHLEG: `${migratedSubtypeBase}.ECHLEG`,
  CLIENT: `${migratedSubtypeBase}.CLIENT`,
  ECRET: `${migratedSubtypeBase}.ECRET`,
  ABD: `${migratedSubtypeBase}.ABD`,
  INT: `${migratedSubtypeBase}.INT`,
  PPV: `${migratedSubtypeBase}.PPV`,
  RSP: `${migratedSubtypeBase}.RSP`,
  VVENT: `${migratedSubtypeBase}.VVENT`,
  VVIND: `${migratedSubtypeBase}.VVIND`,
  VVP: `${migratedSubtypeBase}.VVP`,
  GAG_CASH: `${migratedSubtypeBase}.GAG_CASH`,
  GAG_TITR: `${migratedSubtypeBase}.GAG_TITR`,
  PER_CASH: `${migratedSubtypeBase}.PER_CASH`,
  PER_TITR: `${migratedSubtypeBase}.PER_TITR`,
});
